import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Users, UserPlus, PencilLine, ShieldCheck, Eye, EyeSlash, Check, X, UsersThree} from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { getUsers, createUser, updateUserRole, getRoles, updateRolePermissions } from '../services/api';
import { PhoneInput } from '../components/PhoneInput';
import { TerritorySelect } from '../components/TerritorySelect';
import { toast } from 'sonner';
import { PageHero } from '../components/motion/PageHero';

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  direction: 'Direction',
  commercial: 'Commercial',
  instructeur: 'Instructeur',
  membre_externe: 'Membre externe',
};

const ROLE_COLORS = {
  super_admin: 'bg-[#2D5A4C] text-white',
  direction: 'bg-[#62A8D4] text-white',
  commercial: 'bg-[#E8A365] text-white',
  instructeur: 'bg-[#8DB0A1] text-white',
  membre_externe: 'bg-[#F0EFEB] text-[#5E6D66]',
};

const MODULE_LABELS = {
  contacts: 'Contacts', organizations: 'Organisations', opportunities: 'Opportunites',
  dossiers: 'Dossiers', documents: 'Documents', payments: 'Paiements',
  tasks: 'Taches', partners: 'Partenaires', reporting: 'Reporting', settings: 'Parametres',
};

const ACTION_LABELS = { read: 'Lire', create: 'Creer', update: 'Modifier', delete: 'Supprimer' };

export function UsersPage() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', email: '', password: '', role: 'commercial', telephone: '', telephone_indicatif: '+596', territoire: '' });
  const [creating, setCreating] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editRole, setEditRole] = useState('');
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('users');
  const [editingRole, setEditingRole] = useState(null);
  const [editPerms, setEditPerms] = useState({});
  const [savingPerms, setSavingPerms] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, rolesRes] = await Promise.all([getUsers(), getRoles()]);
      setUsers(usersRes.data.items);
      setRoles(rolesRes.data.items);
      setModules(rolesRes.data.modules);
      setActions(rolesRes.data.actions);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    if (!createForm.name || !createForm.email || createForm.password.length < 6) {
      toast.error('Remplissez tous les champs (mot de passe min 6 caracteres)');
      return;
    }
    setCreating(true);
    try {
      await createUser(createForm);
      toast.success('Utilisateur cree');
      setShowCreate(false);
      setCreateForm({ name: '', email: '', password: '', role: 'commercial', telephone: '', telephone_indicatif: '+596', territoire: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setCreating(false); }
  };

  const handleUpdateRole = async () => {
    if (!editUser) return;
    setSaving(true);
    try {
      await updateUserRole(editUser.id, { role: editRole });
      toast.success('Role mis a jour');
      setEditUser(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const handleToggleActive = async (u) => {
    try {
      await updateUserRole(u.id, { is_active: !u.is_active });
      toast.success(u.is_active ? 'Utilisateur desactive' : 'Utilisateur active');
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  const startEditPerms = (role) => {
    setEditingRole(role.name);
    setEditPerms(JSON.parse(JSON.stringify(role.permissions)));
  };

  const togglePerm = (module, action) => {
    setEditPerms(prev => {
      const updated = { ...prev };
      const current = updated[module] || [];
      if (current.includes(action)) {
        updated[module] = current.filter(a => a !== action);
      } else {
        updated[module] = [...current, action];
      }
      return updated;
    });
  };

  const handleSavePerms = async () => {
    setSavingPerms(true);
    try {
      await updateRolePermissions(editingRole, { permissions: editPerms });
      toast.success('Permissions mises a jour');
      setEditingRole(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSavingPerms(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-[#2D5A4C]" /></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="users-page">
      {/* Hero header — UX transversale v2 */}
      <PageHero
        title="Utilisateurs & Rôles"
        subtitle="Comptes, permissions et administration RBAC"
        icon={<UsersThree size={24} weight="duotone" className="text-white" />}
        gradient="from-[#5E6D66] to-[#2D5A4C]"
        accents={['#00AEEF', '#80C51F']}
      />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#1A2421] tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Utilisateurs & Roles
          </h1>
          <p className="text-[#5E6D66] mt-1">Gestion des acces et permissions</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="create-user-btn">
          <UserPlus size={16} className="mr-2" />
          Nouvel utilisateur
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[#E8E6E1]">
        {[
          { key: 'users', label: 'Utilisateurs', icon: Users },
          { key: 'roles', label: 'Roles & Permissions', icon: ShieldCheck },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            data-testid={`tab-${key}`}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-[1px] ${
              tab === key ? 'border-[#2D5A4C] text-[#2D5A4C]' : 'border-transparent text-[#5E6D66] hover:text-[#1A2421]'
            }`}
          >
            <Icon size={16} />
            {label}
          </button>
        ))}
      </div>

      {/* Users Tab */}
      {tab === 'users' && (
        <Card className="border-[#E8E6E1]" data-testid="users-table">
          <CardContent className="p-0">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#E8E6E1] bg-[#F9F8F5]">
                  <th className="text-left p-3 text-xs font-semibold text-[#5E6D66] uppercase">Nom</th>
                  <th className="text-left p-3 text-xs font-semibold text-[#5E6D66] uppercase">Email</th>
                  <th className="text-left p-3 text-xs font-semibold text-[#5E6D66] uppercase">Role</th>
                  <th className="text-left p-3 text-xs font-semibold text-[#5E6D66] uppercase">Statut</th>
                  <th className="text-left p-3 text-xs font-semibold text-[#5E6D66] uppercase">Derniere activite</th>
                  <th className="text-right p-3 text-xs font-semibold text-[#5E6D66] uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-[#F0EFEB] hover:bg-[#FAFAF8] transition-colors" data-testid={`user-row-${u.email}`}>
                    <td className="p-3">
                      <span className="text-sm font-medium text-[#1A2421]">{u.name}</span>
                    </td>
                    <td className="p-3">
                      <span className="text-sm text-[#5E6D66]">{u.email}</span>
                    </td>
                    <td className="p-3">
                      <Badge className={ROLE_COLORS[u.role] || 'bg-gray-100'}>{ROLE_LABELS[u.role] || u.role}</Badge>
                    </td>
                    <td className="p-3">
                      <Badge variant="outline" className={u.is_active !== false ? 'border-emerald-400 text-emerald-600' : 'border-red-300 text-red-500'}>
                        {u.is_active !== false ? 'Actif' : 'Inactif'}
                      </Badge>
                    </td>
                    <td className="p-3">
                      <span className="text-xs text-[#5E6D66]">
                        {u.last_activity ? `${u.last_activity.action} - ${u.last_activity.module}` : 'Aucune'}
                      </span>
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <Button variant="ghost" size="sm" onClick={() => { setEditUser(u); setEditRole(u.role); }} data-testid={`edit-user-${u.email}`}>
                          <PencilLine size={14} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleActive(u)}
                          className={u.is_active !== false ? 'text-red-500 hover:text-red-600' : 'text-emerald-600 hover:text-emerald-700'}
                          data-testid={`toggle-active-${u.email}`}
                        >
                          {u.is_active !== false ? <EyeSlash size={14} /> : <Eye size={14} />}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Roles Tab */}
      {tab === 'roles' && (
        <div className="space-y-4">
          {roles.map((role) => (
            <Card key={role.name} className="border-[#E8E6E1]" data-testid={`role-card-${role.name}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Badge className={ROLE_COLORS[role.name] || 'bg-gray-100'}>{ROLE_LABELS[role.name] || role.name}</Badge>
                    {role.name === 'super_admin' && (
                      <span className="text-xs text-[#5E6D66]">(non modifiable)</span>
                    )}
                  </div>
                  {role.name !== 'super_admin' && editingRole !== role.name && (
                    <Button variant="outline" size="sm" onClick={() => startEditPerms(role)} data-testid={`edit-perms-${role.name}`}>
                      <PencilLine size={14} className="mr-1" /> Modifier
                    </Button>
                  )}
                  {editingRole === role.name && (
                    <div className="flex gap-1">
                      <Button size="sm" onClick={handleSavePerms} disabled={savingPerms} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid={`save-perms-${role.name}`}>
                        {savingPerms ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check size={14} className="mr-1" />} Sauver
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingRole(null)}><X size={14} /></Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#E8E6E1]">
                        <th className="text-left py-2 px-2 text-[10px] font-semibold text-[#5E6D66] uppercase w-[140px]">Module</th>
                        {actions.map(a => (
                          <th key={a} className="text-center py-2 px-2 text-[10px] font-semibold text-[#5E6D66] uppercase">{ACTION_LABELS[a] || a}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {modules.map(mod => {
                        const perms = editingRole === role.name ? editPerms : role.permissions;
                        const modPerms = perms[mod] || [];
                        return (
                          <tr key={mod} className="border-b border-[#F0EFEB] last:border-0">
                            <td className="py-2 px-2 text-xs text-[#1A2421] font-medium">{MODULE_LABELS[mod] || mod}</td>
                            {actions.map(a => (
                              <td key={a} className="text-center py-2 px-2">
                                {editingRole === role.name ? (
                                  <Switch
                                    checked={modPerms.includes(a)}
                                    onCheckedChange={() => togglePerm(mod, a)}
                                    className="scale-75"
                                    data-testid={`perm-${role.name}-${mod}-${a}`}
                                  />
                                ) : (
                                  modPerms.includes(a) ? (
                                    <Check size={14} className="mx-auto text-[#2D5A4C]" weight="bold" />
                                  ) : (
                                    <X size={14} className="mx-auto text-[#E8E6E1]" />
                                  )
                                )}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create User Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="create-user-dialog">
          <DialogHeader>
            <DialogTitle>Nouvel utilisateur</DialogTitle>
            <DialogDescription className="sr-only">Contenu de la boite de dialogue</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Nom complet</Label>
              <Input value={createForm.name} onChange={(e) => setCreateForm(p => ({ ...p, name: e.target.value }))} data-testid="new-user-name" />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={createForm.email} onChange={(e) => setCreateForm(p => ({ ...p, email: e.target.value }))} data-testid="new-user-email" />
            </div>
            <div className="space-y-2">
              <Label>Mot de passe</Label>
              <Input type="password" value={createForm.password} onChange={(e) => setCreateForm(p => ({ ...p, password: e.target.value }))} data-testid="new-user-password" />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={createForm.role} onValueChange={(v) => setCreateForm(p => ({ ...p, role: v }))}>
                <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ROLE_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Téléphone</Label>
                <PhoneInput
                  indicatif={createForm.telephone_indicatif}
                  number={createForm.telephone}
                  onChange={({ indicatif, number }) => setCreateForm(p => ({ ...p, telephone_indicatif: indicatif, telephone: number }))}
                  testIdPrefix="new-user-tel"
                />
              </div>
              <div>
                <Label>Territoire</Label>
                <TerritorySelect
                  value={createForm.territoire}
                  onChange={(v) => setCreateForm(p => ({ ...p, territoire: v }))}
                  testId="new-user-territoire"
                  placeholder="Sélectionner"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Annuler</Button>
            <Button onClick={handleCreate} disabled={creating} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="submit-create-user">
              {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus size={16} className="mr-2" />}
              Creer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={!!editUser} onOpenChange={() => setEditUser(null)}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle>Modifier le role de {editUser?.name}</DialogTitle>
            <DialogDescription className="sr-only">Contenu de la boite de dialogue</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={editRole} onValueChange={setEditRole}>
                <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ROLE_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUser(null)}>Annuler</Button>
            <Button onClick={handleUpdateRole} disabled={saving} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="submit-edit-role">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check size={16} className="mr-2" />}
              Sauvegarder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
